/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import React from "react";
import { useLocation } from "react-router-dom";
import StandaloneLayout from "@/components/layout/StandaloneLayout";
import "./auth.css";

/**
 * T-3: the shell around every /auth page.
 *
 * These pages deliberately do not render the marketing header, the footer or
 * the support chat, and that stays true. What was missing is that they
 * rendered no chrome at all: a customer landing on the sign-in page from a
 * search result, an email link or the shop had no route back to the site. No
 * logo, no navigation, nothing but the card. The only way out was the browser
 * Back button, which is not a visible control on the page.
 *
 * A logo that links home is the minimum a standalone sign-in page needs.
 *
 * AL-3 (Session 447): the band itself now lives in StandaloneLayout, because
 * My PC Info needs the same thing and the answers here - the logo home, and
 * the language control for a visitor who lands in a language they cannot
 * read - were already the right ones. The markup this file produces is
 * unchanged: the shared component takes the class-name prefix, so every
 * `auth-shell*` rule in auth.css still applies to exactly what it always did.
 */

/**
 * Pages that are on their way somewhere else. The logo is right everywhere;
 * the language control is not. These three redirect as soon as they finish,
 * so a control offering to reload the page mid-redirect is at best pointless
 * and at worst interrupts the flow.
 */
const TRANSIENT_AUTH_PATHS = [
  "/auth/callback",
  "/auth/oauth-complete",
  "/auth/logout",
];

const AuthLayout: React.FC = () => {
  const { pathname } = useLocation();
  const showLanguage = !TRANSIENT_AUTH_PATHS.some((p) => pathname.endsWith(p));

  /* P-4: the language control is kept rather than deleted because it is the
     only way out for a customer who lands here in a language they cannot
     read, and VS-1 in Session 447 reports exactly that happening. The half of
     that fix which would stop them landing here lives in the shopping cart
     and has not shipped. Do not remove this control until it has. */
  return <StandaloneLayout prefix="auth" showLanguage={showLanguage} />;
};

export default AuthLayout;
